import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Maximize2, Eye } from "lucide-react";
import WidgetGridBuilder from "@/components/WidgetGridBuilder";
import { SalarieWidgetLiveBody } from "@/components/widget-bodies/SalarieWidgetLiveBody";

/**
 * Widget Builder salarié — v3.1 : grille libre avec drag + resize + preview live.
 */
export default function SalarieDashboardSettings() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [enabledMap, setEnabledMap] = useState({});
  const [widgetsData, setWidgetsData] = useState(null);
  const [preview, setPreview] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: p }, { data: liveData }] = await Promise.all([
          api.get("/salarie/widgets/catalog"),
          api.get("/salarie/widgets/preferences"),
          api.get("/salarie/widgets/data").catch(() => ({ data: null })),
        ]);
        const cat = c.widgets || [];
        setCatalog(cat);
        const initialPrefs = p.preferences || {};
        setPrefs(initialPrefs);
        setWidgetsData(liveData);
        const em = {};
        cat.forEach((w) => { em[w.key] = !!initialPrefs[w.key]?.enabled; });
        setEnabledMap(em);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (key) => setEnabledMap((m) => ({ ...m, [key]: !m[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const merged = { ...prefs };
      Object.keys(enabledMap).forEach((k) => {
        merged[k] = { ...(merged[k] || {}), enabled: enabledMap[k], order: merged[k]?.order ?? 0 };
      });
      await api.post("/salarie/widgets/preferences", { preferences: merged });
      toast.success("Préférences enregistrées");
      setTimeout(() => navigate("/salarie"), 400);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    const defaults = ["conges_balance", "taux_horaire", "next_workday", "last_bulletin", "unread_messages", "week_planning"];
    const reset = {};
    catalog.forEach((w, i) => {
      reset[w.key] = { enabled: defaults.includes(w.key), order: i, x: null, y: null, w: null, h: null };
    });
    setPrefs(reset);
    setEnabledMap(Object.fromEntries(catalog.map((w) => [w.key, defaults.includes(w.key)])));
    toast.info("Défauts restaurés — cliquez Enregistrer pour valider");
  };

  const enabledCount = Object.values(enabledMap).filter(Boolean).length;

  return (
    <div className="min-h-full" data-testid="salarie-dashboard-settings">
      <PageHeader
        eyebrow="Mon espace"
        title="Personnaliser mon tableau de bord"
        sub={`${enabledCount} widget${enabledCount > 1 ? "s" : ""} activé${enabledCount > 1 ? "s" : ""} sur ${catalog.length} · Glissez pour déplacer, tirez le coin pour redimensionner`}
        right={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/salarie")} data-testid="salarie-widgets-back" className="font-semibold">
              <ArrowLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
            <Button onClick={save} disabled={saving} data-testid="salarie-widgets-save" className="bg-[#002FA7] text-white">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Sauvegarde…" : "Enregistrer"}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10">
        {loading ? (
          <div className="text-sm text-[#868E96]">Chargement…</div>
        ) : (
          <>
            <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="salarie-widgets-toolbar">
              <div className="text-xs text-[#495057] flex items-center gap-2">
                <Maximize2 className="h-3.5 w-3.5" />
                Grille 12 colonnes — les widgets désactivés sont grisés mais visibles pour édition.
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer" data-testid="salarie-widgets-preview-toggle">
                  <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} className="h-3.5 w-3.5 accent-[#002FA7]" />
                  <Eye className="h-3.5 w-3.5" /> Aperçu live
                </label>
                <button onClick={resetDefaults} data-testid="salarie-widgets-reset" className="text-xs underline text-[#868E96] hover:text-[#0A0A0A]">
                  Restaurer les défauts
                </button>
              </div>
            </div>
            <div className="bg-white border border-[#DEE2E6] p-2" data-testid="salarie-widgets-grid-editor">
              <WidgetGridBuilder
                catalog={catalog}
                enabledMap={enabledMap}
                prefs={prefs}
                onLayoutChange={setPrefs}
                onToggle={toggle}
                testidPrefix="salarie"
                renderBody={preview ? (widget) => <SalarieWidgetLiveBody widget={widget} data={widgetsData?.[widget.key]} /> : undefined}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
